import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useCruiseSettings } from '../hooks/useCruiseSettings';
import { DECK_CAPACITY, NAVY } from '../utils/constants';

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const parseDDMMYYYY = (s) => {
  const [d, m, y] = (s || '').split('-').map(Number);
  return new Date(y, m - 1, d);
};

const fmtDate = (ddmmyyyy) => {
  if (!ddmmyyyy) return '';
  const [d, m] = ddmmyyyy.split('-').map(Number);
  return `${d} ${SHORT_MONTHS[m - 1]}`;
};

export default function CapacityAlertsIcon({ onNavigate }) {
  const [alerts, setAlerts]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);

  const { settings: cruiseSettings } = useCruiseSettings();

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const fmtDD = (d) => {
        const y = d.getFullYear(), m = d.getMonth() + 1, dd = d.getDate();
        return `${String(dd).padStart(2,'0')}-${String(m).padStart(2,'0')}-${y}`;
      };
      // Tomorrow through +7 days (exclude today)
      const dates7 = [];
      for (let i = 1; i <= 7; i++) {
        const d = new Date(now); d.setDate(now.getDate() + i);
        dates7.push(fmtDD(d));
      }

      const { data: bookings } = await supabase
        .from('bookings')
        .select('cruise_type, deck, date, adults, children, payment_status')
        .in('date', dates7)
        .neq('payment_status', 'Cancelled');

      const capMap = {};
      (bookings || []).forEach(b => {
        const key = `${b.date}|||${b.cruise_type}|||${b.deck}`;
        capMap[key] = (capMap[key] || 0) + (b.adults || 0) + (b.children || 0);
      });

      const result = [];
      Object.entries(capMap).forEach(([key, pax]) => {
        const [date, cruise, deck] = key.split('|||');
        const max       = DECK_CAPACITY[deck] || 60;
        const setting   = cruiseSettings.find(s => s.cruise_name === cruise);
        const threshold = setting?.capacity_alert_threshold ?? 80;
        const pct = (pax / max) * 100;
        if (pct >= threshold)
          result.push({ date, cruise, deck, pax, max, pct: Math.round(pct) });
      });

      result.sort((a, b) => parseDDMMYYYY(a.date) - parseDDMMYYYY(b.date) || b.pct - a.pct);
      setAlerts(result);
    } catch (_) {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [cruiseSettings]);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const total = alerts.length;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Capacity Alerts"
        style={{
          position: 'relative',
          background: 'none',
          border: '1px solid var(--color-border-strong)',
          borderRadius: 7,
          color: 'var(--color-ink-secondary)',
          fontSize: '0.95rem',
          fontWeight: 800,
          cursor: 'pointer',
          padding: '5px 10px',
          flexShrink: 0,
          lineHeight: 1,
          transition: 'color 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = '#f59e0b'; e.currentTarget.style.borderColor = '#f59e0b'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-ink-secondary)'; e.currentTarget.style.borderColor = 'var(--color-border-strong)'; }}
      >
        !
        {total > 0 && (
          <span style={{
            position: 'absolute', top: -5, right: -5,
            background: '#f59e0b', color: '#fff',
            borderRadius: '50%', minWidth: 17, height: 17,
            fontSize: '0.58rem', fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1.5px solid #fff', padding: '0 3px',
            lineHeight: 1, boxSizing: 'border-box',
          }}>
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="notif-modal">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px 14px', borderBottom: '1px solid var(--color-border-subtle)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 style={{ fontSize: '1rem', fontWeight: 800, color: NAVY, margin: 0 }}>Capacity Alerts</h2>
                {loading && <span style={{ fontSize: '0.65rem', color: 'var(--color-ink-faint)' }}>Updating…</span>}
                {total > 0 && !loading && (
                  <span style={{ background: '#f59e0b', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: '0.65rem', fontWeight: 800 }}>{total}</span>
                )}
              </div>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: 'var(--color-ink-muted)', padding: '0 4px', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '16px 22px 20px' }}>
              {total === 0 && !loading && (
                <div style={{ fontSize: '0.88rem', color: '#15803d', fontWeight: 600, textAlign: 'center', padding: '36px 0' }}>
                  No capacity alerts ✓
                </div>
              )}
              {alerts.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--color-ink-faint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                    Near Capacity — Next 7 Days ({total})
                  </div>
                  {alerts.map((n, i) => {
                    const isRed       = n.pct >= 90;
                    const accentColor = isRed ? '#dc2626' : '#f59e0b';
                    const bg          = isRed ? '#fff1f2' : '#fffbeb';
                    const borderCol   = isRed ? '#fca5a5' : '#fcd34d';
                    const textColor   = isRed ? '#991b1b' : '#92400e';
                    return (
                      <div key={`${n.date}|${n.cruise}|${n.deck}`}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: bg, border: `1px solid ${borderCol}`, borderRadius: 8, padding: '10px 14px', marginBottom: i < alerts.length - 1 ? 8 : 0 }}>
                        <div>
                          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: textColor }}>{n.cruise} · {n.deck}</div>
                          <div style={{ fontSize: '0.73rem', color: textColor, opacity: 0.85, marginTop: 2 }}>{fmtDate(n.date)} · {n.pax}/{n.max} pax · {n.pct}%</div>
                        </div>
                        <button
                          onClick={() => { setOpen(false); onNavigate('daily-tracker', n.date, { cruise_type: n.cruise }); }}
                          style={{ flexShrink: 0, marginLeft: 12, fontSize: '0.72rem', fontWeight: 700, color: '#fff', background: accentColor, border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          → Tracker
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
